import React, { useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { Loader } from "../../../Shared/Loader";
import {
  Card, CardHead, Tiles, Tile, Stacked, Muted, Numeric,
  GhostButton, GridWrap, Empty, dataGridSx, StatusPill, StaffStatus,
} from "../staffStyles";
import { getStaffStatus, STAFF_STATUS } from "../../../Shared/staffStatus";

/**
 * The people who report to you, and the two ways to hand them clients.
 *
 * Two things were wrong with the original and are fixed here:
 *
 * 1. The header read `staff?.subordinates?.length`, but the query returns an
 *    array — it is iterated with forEach directly below. So the team count
 *    rendered blank every time. It now counts the rows actually shown.
 *
 * 2. Both action buttons were labelled "Assign Clients". One opens a manual
 *    picker, the other an Excel upload. Identical labels for different actions
 *    is how someone bulk-uploads when they meant to pick one person, so they
 *    are now named for what they do and carry distinct icons.
 */
export const ManageTeamTable = ({
  team = [],
  isFetching,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  onAssignClients,
  onAssignFromExcel,
}) => {
  const rows = useMemo(
    () =>
      (team || []).map((member) => ({
        id: member._id,
        empID: member.empID,
        name: `${member.firstName || ""} ${member.lastName || ""}`.trim(),
        email: member.email,
        role: member.role,
        designation: member.designation,
        phone: member.phoneNum,
        city: member.address,
        // The endpoint only returns people who still work here, so this is
        // active or blocked — never removed. Derived through the shared helper
        // anyway, so this row means the same thing as a Manage Staff row.
        status: getStaffStatus(member),
      })),
    [team]
  );

  const suspendedCount = useMemo(
    () => rows.filter((row) => row.status === STAFF_STATUS.BLOCKED).length,
    [rows]
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Team member",
        flex: 1,
        minWidth: 200,
        renderCell: (params) => (
          <Stacked>
            <span className="primary">{params.value || "—"}</span>
            {params.row.email ? (
              <span className="sub">{params.row.email}</span>
            ) : null}
          </Stacked>
        ),
      },
      {
        field: "role",
        headerName: "Designation",
        width: 140,
        renderCell: (params) => (
          <Stacked>
            <span
              className="primary"
              style={{ textTransform: "uppercase", fontSize: "0.72rem", letterSpacing: "0.5px" }}
            >
              {params.value || "—"}
            </span>
            {params.row.designation && params.row.designation !== params.value ? (
              <span className="sub">{params.row.designation}</span>
            ) : null}
          </Stacked>
        ),
      },
      {
        // Sits immediately after the name rather than at the far right, where
        // it would be off-screen on a laptop at the moment someone clicks
        // "Choose clients" — which is the one moment it has to be read.
        field: "status",
        headerName: "Status",
        width: 116,
        renderCell: (params) => {
          const meta = StaffStatus[params.value];
          if (!meta) return <Muted>—</Muted>;
          return (
            <StatusPill $color={meta.color} $bg={meta.bg}>
              {meta.label}
            </StatusPill>
          );
        },
      },
      {
        field: "phone",
        headerName: "Phone",
        width: 152,
        renderCell: (params) =>
          params.value ? <Numeric>{params.value}</Numeric> : <Muted>—</Muted>,
      },
      {
        field: "city",
        headerName: "City",
        width: 130,
        renderCell: (params) => params.value || <Muted>—</Muted>,
      },
      {
        field: "assign",
        headerName: "Hand over clients",
        width: 268,
        sortable: false,
        filterable: false,
        hideable: false,
        renderCell: (params) => {
          const isBlocked = params.row.status === STAFF_STATUS.BLOCKED;

          // Blocked people are assignable on purpose, but the tooltip has to
          // say what will happen: they cannot log in, so the clients and the
          // notification both wait for them. Stated rather than prevented —
          // parking a book with someone who is coming back is a real thing to
          // want, and the CEO is the one who decides.
          const suffix = isBlocked
            ? " — they are suspended and cannot log in, so this waits until they are unblocked"
            : "";

          return (
            <div style={{ display: "flex", gap: 6 }}>
              <Tooltip title={`Pick clients one by one and assign them${suffix}`}>
                <GhostButton
                  type="button"
                  style={{ minHeight: 34, padding: "6px 11px", fontSize: "0.76rem" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAssignClients(params.row);
                  }}
                >
                  <PersonAddAltOutlinedIcon />
                  Choose clients
                </GhostButton>
              </Tooltip>

              <Tooltip
                title={`Upload a spreadsheet to assign many clients at once${suffix}`}
              >
                <GhostButton
                  type="button"
                  style={{ minHeight: 34, padding: "6px 11px", fontSize: "0.76rem" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAssignFromExcel(params.row);
                  }}
                >
                  <UploadFileOutlinedIcon />
                  From Excel
                </GhostButton>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [onAssignClients, onAssignFromExcel]
  );

  return (
    <>
      <Tiles>
        <Tile>
          <p className="label">Team size</p>
          <div className="figure">{rows.length}</div>
          <p className="meta">People reporting to you</p>
        </Tile>
        <Tile>
          <p className="label">Can work now</p>
          <div className="figure">{rows.length - suspendedCount}</div>
          <p className="meta">Able to log in and act on what you assign</p>
        </Tile>
        {suspendedCount > 0 ? (
          <Tile>
            <p className="label">Suspended</p>
            <div className="figure">{suspendedCount}</div>
            <p className="meta">
              Still on the team and assignable, but cannot log in yet
            </p>
          </Tile>
        ) : null}
      </Tiles>

      <Card>
        <CardHead>
          <span className="count">
            Showing <strong>{rows.length}</strong>{" "}
            {rows.length === 1 ? "team member" : "team members"}
          </span>
        </CardHead>

        <GridWrap>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={isFetching}
            disableSelectionOnClick
            page={page}
            onPageChange={onPageChange}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            pagination
            rowsPerPageOptions={[25, 50, 100]}
            components={{
              NoRowsOverlay: () => (
                <Empty>
                  <GroupsOutlinedIcon />
                  <h3>Nobody reports to you yet</h3>
                  <p>
                    Team members appear here once they are placed under you in
                    the team structure.
                  </p>
                </Empty>
              ),
            }}
            sx={dataGridSx}
          />
          {isFetching ? <Loader /> : null}
        </GridWrap>
      </Card>
    </>
  );
};
